<script setup lang="ts">
// /cost-guides — M6, unblocked by the open-items review (item 1).
//
// The mockup framed this page as a pricing authority. It is framed here as
// the opposite: a page whose main job is to give a homeowner enough context
// to judge a quote, while repeatedly making clear that it cannot produce
// one. The disclaimer appears three times — under the hero, above the
// repair table, and beside the replacement range — because a reader who
// lands mid-page on a deep link has to meet it too.
import { RouterLink } from 'vue-router'

import {
    PRICE_DISCLAIMER,
    QUOTE_QUESTIONS,
    REPAIRS,
    REPLACEMENT_FACTORS,
    REPLACEMENT_RANGE,
} from '../content/costs'

const usd = (n: number) => `$${n.toLocaleString('en-US')}`
</script>

<template>
    <section class="hero">
        <div class="hero-inner">
            <p class="crumb">Home <span>›</span> Cost Guides</p>
            <h1>What HVAC Work <span class="hl">Generally Costs</span></h1>
            <p class="lede">
                Broad, educational ranges for the repairs homeowners ask about most, and a planning
                range for a full system replacement. Enough to know whether a quote is in the right
                neighbourhood — and honest about the fact that only a site visit produces a real
                number.
            </p>

            <div class="jump">
                <a href="#repairs">Common repairs</a>
                <a href="#new-system">Replacing the system</a>
                <a href="#questions">Questions to ask</a>
            </div>
        </div>
    </section>

    <!-- Stated once at the top, in full, before a single figure appears. -->
    <section class="disclaimer-band">
        <div class="band-inner">
            <span class="mark" aria-hidden="true">!</span>
            <div>
                <strong>Read this before the numbers.</strong>
                <p>{{ PRICE_DISCLAIMER }}</p>
            </div>
        </div>
    </section>

    <section id="repairs" class="repairs">
        <div class="repairs-inner">
            <h2>Common repair ranges</h2>
            <p class="sub">
                Each range below is wide because the real spread is wide. The same repair on two
                different systems can differ by thousands, and the notes explain what moves it.
            </p>

            <div class="repair-list">
                <article v-for="r in REPAIRS" :key="r.id" class="repair">
                    <header>
                        <h3>{{ r.name }}</h3>
                        <p class="range">
                            <span class="figures">{{ usd(r.low) }} – {{ usd(r.high) }}</span>
                            <small>estimated range</small>
                        </p>
                    </header>
                    <p class="what">{{ r.what }}</p>
                    <p class="drivers"><strong>What moves the price:</strong> {{ r.drivers }}</p>
                </article>
            </div>

            <p class="restate">
                These are educational estimates, not quotes. A contractor pricing outside a range on
                this page is not automatically overcharging — they are pricing your actual system,
                in your actual house, which this page cannot see.
            </p>
        </div>
    </section>

    <section id="new-system" class="replace">
        <div class="replace-inner">
            <div class="replace-lede">
                <h2>Replacing the whole system</h2>
                <p class="body">
                    There is no useful tonnage-by-tonnage price list, and this page will not print
                    one. What is useful is the size of the decision and what actually drives it.
                </p>

                <div class="planning">
                    <small>General planning range</small>
                    <strong>
                        {{ usd(REPLACEMENT_RANGE.low) }} – {{ usd(REPLACEMENT_RANGE.high) }}+
                    </strong>
                    <p>
                        A broad planning figure for a complete replacement — not a quote, and not a
                        cap. Complex or premium installations can exceed it.
                    </p>
                </div>
            </div>

            <ol class="factors">
                <li v-for="f in REPLACEMENT_FACTORS" :key="f.n">
                    <span class="n">{{ f.n }}</span>
                    <div>
                        <strong>{{ f.title }}</strong>
                        <p>{{ f.body }}</p>
                        <ul>
                            <li v-for="ex in f.examples" :key="ex">{{ ex }}</li>
                        </ul>
                    </div>
                </li>
            </ol>
        </div>
    </section>

    <section class="disclaimer-band quiet">
        <div class="band-inner">
            <span class="mark" aria-hidden="true">!</span>
            <div>
                <strong>Every figure on this page is an estimate.</strong>
                <p>{{ PRICE_DISCLAIMER }}</p>
            </div>
        </div>
    </section>

    <section id="questions" class="questions">
        <div class="questions-inner">
            <div>
                <h2>Questions worth asking about any quote</h2>
                <p class="sub">
                    Whatever number you are looking at, these five tell you a lot about who you are
                    dealing with. A thorough contractor will answer all of them without hesitating.
                </p>
            </div>
            <ol class="qlist">
                <li v-for="q in QUOTE_QUESTIONS" :key="q">{{ q }}</li>
            </ol>
        </div>
    </section>

    <section class="closing">
        <div class="closing-inner">
            <h2>Holding a quote you are not sure about?</h2>
            <p>
                Run the repair-or-replace numbers first, or send the details over and have an HVAC
                professional look at it with you. Nothing needs an account.
            </p>
            <div class="actions">
                <RouterLink class="cta" to="/repair-or-replace">
                    Run the repair-or-replace check →
                </RouterLink>
                <RouterLink class="cta cta--ghost" to="/second-opinion">
                    Connect With an HVAC Professional
                </RouterLink>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
/* ---- hero ---- */
.hero {
    background: var(--site-gradient-navy);
    color: #fff;
}

.hero-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg);
}

.crumb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.62);

    span {
        margin: 0 0.3rem;
    }
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.7rem, 3.3vw, 2.5rem);
    font-weight: 800;
    line-height: 1.16;
    margin: 0 0 var(--site-space-md);
}

.hl {
    color: var(--site-accent);
}

.lede {
    max-width: 42rem;
    margin: 0;
    color: rgba(255, 255, 255, 0.86);
    line-height: 1.65;
}

.jump {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: var(--site-space-lg);

    a {
        display: inline-flex;
        align-items: center;
        padding: 0.4rem 0.85rem;
        border-radius: var(--site-radius-pill);
        border: 1px solid rgba(255, 255, 255, 0.24);
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.8rem;
        font-weight: 600;
        text-decoration: none;

        &:hover {
            border-color: var(--site-accent);
            color: #fff;
        }

        @media (max-width: 860px) {
            min-height: 44px;
            padding: 0 1rem;
        }
    }
}

/* ---- the disclaimer band ---- */
.disclaimer-band {
    background: var(--site-accent-soft);
    border-top: 1px solid var(--site-border);
    border-bottom: 1px solid var(--site-border);
}

.band-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-lg);
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.95rem;
        color: var(--site-navy);
        margin-bottom: 0.25rem;
    }

    p {
        margin: 0;
        max-width: 62rem;
        font-size: 0.86rem;
        line-height: 1.65;
        color: var(--site-text-muted);
    }
}

.mark {
    display: grid;
    place-items: center;
    width: 1.7rem;
    height: 1.7rem;
    flex-shrink: 0;
    border-radius: 50%;
    background: var(--site-accent);
    color: #fff;
    font-family: var(--site-font-heading);
    font-weight: 800;
    font-size: 0.95rem;
}

.quiet {
    background: var(--site-bg-alt);
}

/* ---- shared ---- */
h2 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.3rem, 2.4vw, 1.7rem);
    font-weight: 700;
    color: var(--site-navy);
    margin: 0 0 0.4rem;
}

.sub {
    margin: 0 0 var(--site-space-lg);
    max-width: 46rem;
    color: var(--site-text-muted);
    font-size: 0.92rem;
    line-height: 1.6;
}

.repairs-inner,
.replace-inner,
.questions-inner,
.closing-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

/* ---- repairs ---- */
.repair-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 26rem), 1fr));
    gap: var(--site-space-md);
}

.repair {
    padding: var(--site-space-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);

    header {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: baseline;
        justify-content: space-between;
        padding-bottom: 0.6rem;
        margin-bottom: 0.7rem;
        border-bottom: 1px solid var(--site-border);
    }

    h3 {
        font-family: var(--site-font-heading);
        font-size: 1rem;
        font-weight: 700;
        color: var(--site-navy);
        margin: 0;
    }

    p {
        margin: 0 0 0.6rem;
        font-size: 0.85rem;
        line-height: 1.62;
        color: var(--site-text-muted);

        &:last-child {
            margin-bottom: 0;
        }
    }

    .drivers strong {
        color: var(--site-navy);
    }
}

.range {
    margin: 0 !important;
    text-align: right;

    .figures {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 1.02rem;
        font-weight: 800;
        color: var(--site-blue);
        white-space: nowrap;
    }

    small {
        font-size: 0.68rem;
        text-transform: uppercase;
        letter-spacing: 0.09em;
        color: var(--site-text-muted);

        /* "ESTIMATED RANGE" is the qualifier that stops a figure reading
           as a quote. It does not get to be the smallest text on the
           page on the device most people will read it on. */
        @media (max-width: 860px) {
            font-size: 0.74rem;
        }
    }
}

.restate {
    margin: var(--site-space-lg) 0 0;
    padding: 0.8rem 1rem;
    border-left: 3px solid var(--site-accent);
    background: var(--site-bg-alt);
    border-radius: var(--site-radius-sm);
    font-size: 0.86rem;
    line-height: 1.6;
    color: var(--site-text-muted);
}

/* ---- replacement ---- */
.replace {
    background: var(--site-bg-alt);
}

.replace-inner {
    display: grid;
    grid-template-columns: 22rem minmax(0, 1fr);
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }

    .body {
        margin: 0;
        font-size: 0.92rem;
        line-height: 1.6;
        color: var(--site-text-muted);
    }
}

.planning {
    margin-top: var(--site-space-lg);
    padding: var(--site-space-md);
    background: var(--site-navy);
    border-radius: var(--site-radius-md);
    color: #fff;

    small {
        display: block;
        font-size: 0.68rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: rgba(255, 255, 255, 0.64);

        @media (max-width: 860px) {
            font-size: 0.74rem;
        }
    }

    strong {
        display: block;
        margin: 0.25rem 0 0.45rem;
        font-family: var(--site-font-heading);
        font-size: clamp(1.3rem, 2.6vw, 1.75rem);
        font-weight: 800;
        color: var(--site-accent);
    }

    p {
        margin: 0;
        font-size: 0.8rem;
        line-height: 1.55;
        color: rgba(255, 255, 255, 0.78);
    }
}

.factors {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: var(--site-space-md);

    > li {
        display: flex;
        gap: 0.85rem;
        padding: var(--site-space-md);
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        border-radius: var(--site-radius-md);
    }

    .n {
        display: grid;
        place-items: center;
        width: 1.9rem;
        height: 1.9rem;
        flex-shrink: 0;
        border-radius: 50%;
        background: var(--site-blue);
        color: #fff;
        font-family: var(--site-font-heading);
        font-size: 0.85rem;
        font-weight: 700;
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.98rem;
        color: var(--site-navy);
    }

    > li > div > p {
        margin: 0.2rem 0 0.6rem;
        font-size: 0.86rem;
        line-height: 1.6;
        color: var(--site-text-muted);
    }

    ul {
        margin: 0;
        padding-left: 1.1rem;
        display: grid;
        gap: 0.3rem;

        li {
            font-size: 0.83rem;
            line-height: 1.55;
            color: var(--site-text-muted);
        }
    }
}

/* ---- questions ---- */
.questions-inner {
    display: grid;
    grid-template-columns: 20rem minmax(0, 1fr);
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
}

.qlist {
    margin: 0;
    padding-left: 1.2rem;
    display: grid;
    gap: var(--site-space-sm);

    li {
        padding-left: 0.3rem;
        font-size: 0.92rem;
        line-height: 1.6;
        color: var(--site-navy);

        &::marker {
            color: var(--site-accent);
            font-weight: 700;
        }
    }
}

/* ---- closing ---- */
.closing {
    background: var(--site-bg-alt);
}

.closing-inner {
    text-align: center;
    max-width: 44rem;

    p {
        margin: 0 auto var(--site-space-lg);
        max-width: 34rem;
        color: var(--site-text-muted);
        line-height: 1.6;
    }
}

.actions {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: var(--site-space-sm);
}

.cta {
    padding: 0.75rem 1.4rem;
    border-radius: var(--site-radius-sm);
    background: var(--site-gradient-cta);
    color: #fff;
    font-weight: 700;
    font-size: 0.92rem;
    text-decoration: none;

    &:hover {
        background: var(--site-accent-strong);
    }

    &--ghost {
        background: none;
        border: 1px solid var(--site-border);
        color: var(--site-navy);

        &:hover {
            background: none;
            border-color: var(--site-blue);
            color: var(--site-blue);
        }
    }
}
</style>
